// src/hooks/useAnalysisPolling.ts

import { useEffect, useRef, useState } from "react";

/**
 * 86d3vhb3j S6 — a test order created from an uploaded document now sits honestly in
 * `in_progress` until its analysis pipeline finishes, so a page opened while one is
 * running would otherwise show "In Progress" forever with no signal that anything
 * changed.
 *
 * Same shape as the `pollRef` + `setInterval` pattern already used by
 * PatientAnalysisSection and SupplementsTab — only slower and bounded:
 *
 *  - SLOWER, because the blood pipeline's job timeout is 5400s and a realistic run is
 *    several minutes. PatientAnalysisSection's 4s tick would be thousands of requests
 *    per upload.
 *  - BOUNDED, because a stuck order left open in a tab overnight must not poll forever.
 *    Once the bound elapses the caller surfaces a manual refresh instead.
 */

/** 20s — cheap over a multi-minute run, still feels live to someone watching. */
export const ANALYSIS_POLL_INTERVAL_MS = 20_000;

/** ~15 minutes of polling, after which the surface offers a manual refresh. */
export const ANALYSIS_POLL_MAX_MS = 15 * 60_000;

interface UseAnalysisPollingOptions {
	/** Poll ONLY while this is true — i.e. while something on screen is genuinely in progress. */
	active: boolean;
	/** Re-run whatever loader the surface already owns. Never a new endpoint. */
	onPoll: () => void | Promise<void>;
	intervalMs?: number;
	maxDurationMs?: number;
}

/**
 * Returns `exhausted` — true once the bound elapsed with the surface still in progress.
 * It re-arms automatically as soon as `active` goes false, so the next in-flight order
 * polls again.
 */
export const useAnalysisPolling = ({
	active,
	onPoll,
	intervalMs = ANALYSIS_POLL_INTERVAL_MS,
	maxDurationMs = ANALYSIS_POLL_MAX_MS,
}: UseAnalysisPollingOptions): { exhausted: boolean } => {
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	// Held in a ref so a caller passing an inline closure cannot restart the interval —
	// and reset the bound with it — on every render.
	const onPollRef = useRef(onPoll);
	const [exhausted, setExhausted] = useState(false);

	useEffect(() => {
		onPollRef.current = onPoll;
	}, [onPoll]);

	useEffect(() => {
		if (!active) setExhausted(false);
	}, [active]);

	useEffect(() => {
		if (!active || exhausted) {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
			return;
		}

		const startedAt = Date.now();
		pollRef.current = setInterval(() => {
			if (Date.now() - startedAt >= maxDurationMs) {
				if (pollRef.current) {
					clearInterval(pollRef.current);
					pollRef.current = null;
				}
				setExhausted(true);
				return;
			}
			void onPollRef.current();
		}, intervalMs);

		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, [active, exhausted, intervalMs, maxDurationMs]);

	return { exhausted };
};

export default useAnalysisPolling;
